import {
  Divider,
  Tooltip
} from '@eggplant/pattern-library'
import {
  SelectModel,
  SelectSuite
} from '@eggplant/input-controls'
import type {
  CSSProperties,
  ReactNode
} from 'react'
import type {
  DropdownPlacement,
  TestResultQuery
} from '@eggplant/types'

interface Props {
  isDisabled?: boolean
  hasBridgeDivider?: boolean
  modelId?: string
  suiteId?: string
  isShowSubmodel?: boolean
  isFilterFixedMinMaxWidth?: boolean
  onChange: (value: TestResultQuery) => void
  externalQuery?: ReactNode
  onClearSubmodelToggleButton?: () => void
  dropdownPlacement?: DropdownPlacement
}

const baseWrapperStyle: CSSProperties = {
  display: 'grid',
  gridColumnGap: '12px',
  alignItems: 'center'
}

const wrapperDependentStyle: CSSProperties = {
  ...baseWrapperStyle,
  gridColumnGap: 0,
  padding: 'none'
}

const horizontalDividerWrapperStyle: CSSProperties = {
  width: '12px'
}

const verticalDividerWrapperStyle: CSSProperties = {
  display: 'grid',
  height: '8px',
  justifyContent: 'center'
}

const initialModelOptions = [
  {
    label: 'Only models (Hide suites)',
    value: 'ALL_MODEL'
  }
]

const initialSuiteOptions = [
  {
    label: 'Only suites (Hide models)',
    value: 'ALL_SUITES'
  }
]

const getDividerProps = (isDropdownPlacementBottom: boolean) => isDropdownPlacementBottom
  ? { isCenter: true }
  : { isVertical: true }

const SelectModelSuite = ({
  isDisabled = false,
  hasBridgeDivider = true,
  modelId,
  suiteId,
  onChange,
  externalQuery,
  onClearSubmodelToggleButton,
  isShowSubmodel,
  isFilterFixedMinMaxWidth = true,
  dropdownPlacement = 'bottom'
// eslint-disable-next-line complexity
}: Props) => {
  const isDropdownPlacementBottom = dropdownPlacement === 'bottom'

  const handleModelChange = (value: string) => {
    onChange({
      model_id: value,
      asset_id: ''
    })
  }

  const handleSuiteChange = (value: string) => {
    onChange({
      model_id: '',
      asset_id: value
    })
  }

  const wrapperStyle = isDropdownPlacementBottom
    ? {
      ...wrapperDependentStyle,
      gridAutoFlow: 'column' // one row
    }
    : {
      ...wrapperDependentStyle,
      gridAutoFlow: 'row' // one column
    }

  const dividerWrapperStyle = isDropdownPlacementBottom
    ? horizontalDividerWrapperStyle
    : verticalDividerWrapperStyle

  return (
    <div style={wrapperStyle}>
      <SelectModel
        dropdownPlacement={dropdownPlacement}
        externalQuery={externalQuery}
        initialOptions={initialModelOptions}
        isDisabled={isDisabled}
        isFilter
        isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
        isShowSubmodel={isShowSubmodel}
        onChange={handleModelChange}
        onClearSubmodelToggleButton={onClearSubmodelToggleButton}
        placeholder='Model'
        shortErrorMessage='Failed to load models'
        value={modelId || ''}
      />
      {
        hasBridgeDivider &&
          <Tooltip label='Only one filter can be selected in the linked filters'>
            <div style={dividerWrapperStyle}>
              <Divider {...getDividerProps(isDropdownPlacementBottom)} />
            </div>
          </Tooltip>
      }
      <SelectSuite
        dropdownPlacement={dropdownPlacement}
        initialOptions={initialSuiteOptions}
        isDisabled={isDisabled}
        isFilter
        isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
        onChange={handleSuiteChange}
        placeholder='Suite'
        shortErrorMessage='Failed to load suites'
        value={suiteId || ''}
      />
    </div>
  )
}

export default SelectModelSuite
